import { useState } from 'react'
import { X, Tablet, Lock, Eye, Trash2, Copy } from '@/components/icons'
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from './ui/drawer'
import { PrntrCard, PrntrInput, PrntrValue, prntrInputClass } from './ui/prntr-input'
import { Button } from './ui/button'
import { Card } from '@/components/ui/card'
import {
  useAircraftGetDevicePasswords,
  useAircraftAddDevicePassword,
  useAircraftRemoveDevicePassword,
} from '../lib/hooks/aircraft'
import { toast } from 'sonner'

interface DevicePasswordsDrawerProps {
  aircraftId: string
  open: boolean
  onClose: () => void
}

export function DevicePasswordsDrawer({
  aircraftId,
  open,
  onClose,
}: DevicePasswordsDrawerProps) {
  const [newDeviceName, setNewDeviceName] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [visiblePasswords, setVisiblePasswords] = useState<Set<string>>(
    new Set()
  )

  const { data: passwords = [], isPending: loading } =
    useAircraftGetDevicePasswords(aircraftId)
  const addPassword = useAircraftAddDevicePassword(aircraftId)
  const removePassword = useAircraftRemoveDevicePassword(aircraftId)

  const handleAddPassword = async () => {
    if (!newDeviceName.trim() || !newPassword.trim()) {
      toast.error('Please enter both device name and password')
      return
    }

    try {
      await addPassword.mutateAsync({
        aircraftId,
        deviceName: newDeviceName.trim(),
        password: newPassword.trim(),
      })
      toast.success('Password added')
      setNewDeviceName('')
      setNewPassword('')
    } catch (error) {
      console.error('Error adding device password:', error)
      toast.error('Failed to add password')
    }
  }

  const handleDeletePassword = async (id: string) => {
    try {
      await removePassword.mutateAsync({
        devicePasswordId: id,
        aircraftId,
      })
      toast.success('Password deleted')
    } catch (error) {
      console.error('Error deleting device password:', error)
      toast.error('Failed to delete password')
    }
  }

  const handleToggleVisibility = (id: string) => {
    setVisiblePasswords((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(id)) {
        newSet.delete(id)
      } else {
        newSet.add(id)
      }
      return newSet
    })
  }

  const handleCopyPassword = async (password: string) => {
    try {
      await navigator.clipboard.writeText(password)
      toast.success('Password copied')
    } catch {
      toast.error('Failed to copy password')
    }
  }

  return (
    <Drawer open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DrawerContent className="bg-surface-L0 max-h-[85dvh]">
        <DrawerHeader className="flex flex-row items-center justify-between p-4">
          <DrawerTitle className="text-lg font-semibold">
            Device Passwords
          </DrawerTitle>
          <DrawerClose asChild>
            <Button variant="secondary" size="icon" roundness="round" className="size-8">
              <X className="w-4 h-4" />
            </Button>
          </DrawerClose>
        </DrawerHeader>

        <div
          className="min-h-0 flex-1 overflow-y-auto px-4 pb-8"
          style={{ WebkitOverflowScrolling: 'touch' }}
        >
          <div className="flex flex-col gap-4">
            {/* Add Password Form */}
            <PrntrCard>
              <PrntrInput label="Device Name" iconLeft={<Tablet />} empty={!newDeviceName}>
                <input
                  type="text"
                  placeholder="e.g., Cabin WiFi"
                  value={newDeviceName}
                  onChange={(e) => setNewDeviceName(e.target.value)}
                  className={prntrInputClass}
                />
              </PrntrInput>
              <PrntrInput label="Password" iconLeft={<Lock />} empty={!newPassword}>
                <input
                  type="text"
                  placeholder="Enter password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className={prntrInputClass}
                />
              </PrntrInput>
            </PrntrCard>

            <Button
              onClick={handleAddPassword}
              disabled={addPassword.isPending}
              className="w-full"
            >
              {addPassword.isPending ? 'Adding...' : 'Add Password'}
            </Button>

            {/* Password List */}
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <div className="size-8 border-4 border-muted border-t-primary rounded-full animate-spin" />
              </div>
            ) : passwords.length === 0 ? (
              <div className="bg-surface-L2 rounded-lg p-8 text-center">
                <Lock className="size-12 text-content-primary-subtle/30 mx-auto mb-3" />
                <p className="text-sm text-content-primary-subtle">
                  No passwords saved yet
                </p>
              </div>
            ) : (
              passwords.map((password) => (
                <Card
                  key={password.id}
                  className="p-4 flex flex-col gap-2"
                >
                  <PrntrInput label={password.deviceName} className="border-b-0">
                    <PrntrValue
                      value={
                        visiblePasswords.has(password.id) ? password.password : '••••••••••'
                      }
                    />
                  </PrntrInput>
                  <div className="flex gap-2 items-center justify-end">
                    <button
                      onClick={() => handleToggleVisibility(password.id)}
                      className="size-9 flex items-center justify-center bg-surface-L2 rounded-lg hover:bg-surface-L2 transition-colors"
                    >
                      <Eye className="size-4 text-content-primary-bold" />
                    </button>
                    <button
                      onClick={() => handleCopyPassword(password.password)}
                      className="size-9 flex items-center justify-center bg-surface-L2 rounded-lg hover:bg-surface-L2 transition-colors"
                    >
                      <Copy className="size-4 text-content-primary-bold" />
                    </button>
                    <button
                      onClick={() => handleDeletePassword(password.id)}
                      className="size-9 flex items-center justify-center bg-content-negative-bold rounded-lg hover:bg-content-negative-bold/90 transition-colors"
                    >
                      <Trash2 className="size-4 text-white" />
                    </button>
                  </div>
                </Card>
              ))
            )}
          </div>
        </div>
      </DrawerContent>
    </Drawer>
  )
}
